import React, { useState, useLayoutEffect, Fragment } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useConfiguration } from 'contexts/configuration'
import { useGetDisclaimers } from './disclaimers'
import { size, omit } from 'utilities/object'
import { codify } from 'pages/integrations/pages/integration/services/utilities'
import StringField from 'components/form/field/string'
import SelectField from 'components/form/field/select'
import TimeField from 'components/form/field/time'
import CheckboxField from 'components/form/field/checkbox'
import { UnsetButton } from './s'
import { Helper } from 'components/form/field/s'
import { InlineMessage } from 'components/message'
import Divider from 'components/divider'

const typeToFieldMap = {
    string: StringField,
    url: StringField,
    datetime: TimeField,
    boolean: CheckboxField,
    enum: SelectField
}

const typeToPropsMap = {
    boolean: {
        interaction: 'switch'
    },
    url: {
        controlProps: {
            type: 'url'
        }
    }
}

const typeToFieldPropsMap = {
    boolean: {
        include: 'always'
    },
    datetime: {
        include: 'always'
    },
    enum: {
        include: 'always'
    }
}

export const useIntegrationFieldRenderer = ({ integration, divider = true, salt }) => {
    const { formatMessage } = useIntl()

    const [localValues, setLocalValues] = useState({})
    const [unsetFields, setUnsetFields] = useState({})

    const [disclaimers, meta] = useGetDisclaimers()(integration)

    return ({
        name: fieldName,
        type: fieldType,
        value = null,
        options,
        hasPersistedValue,
        required,
        secret,
        label,
        error,
        onChange,
        isLastField = false,
        ref,
        controlProps,
        ...props
    }) => {
        const Field = typeToFieldMap[fieldType] ?? StringField
        const typeProps = typeToPropsMap[fieldType] ?? null
        const fieldProps = typeToFieldPropsMap[fieldType] ?? null

        const isConnected = ['connected', 'active'].includes(integration.status)

        const update = (value, unset = false) => {
            setLocalValues(localValues => ({
                ...localValues,
                [fieldName]: value
            }))

            setUnsetFields(unsetFields => {
                if(!unset) {
                    return omit(unsetFields, fieldName)
                }

                return {
                    ...unsetFields,
                    [fieldName]: true
                }
            })

            onChange({ [fieldName]: value })
        }

        required = (fieldType === 'boolean') ?
            false :
            required

        value ??= options?.find(({ selected }) => selected)?.key ?? null

        if(!!secret) {
            value = null
        }

        const localValue = localValues?.[fieldName]
        const unset = !!unsetFields?.[fieldName]
        const unsettable = (fieldType !== 'boolean') && !required && !unset && hasPersistedValue
        const disabled = fieldName === 'createNewAbsencesInIntegration' && !meta.absenceConfigured

        controlProps = {
            ...controlProps,
            ...((secret === 'filled' && !unset) ? {
                placeholder: '••••••••••••••••'
            } : null)
        }

        const fieldNameLabel = getFieldNameLabel(integration, fieldName)
        const disclaimer = disclaimers?.[fieldName] ?? {}

        if(!label) {
            label = formatMessage({
                id: `integration_label_${fieldNameLabel}`,
                defaultMessage: fieldNameLabel
            }, {
                service: integration.name,
                code: codify
            })
        }

        const helperTranslationKey = `integration_helper_${fieldNameLabel}`
        const helper = formatMessage({
            id: helperTranslationKey,
            defaultMessage: helperTranslationKey
        })

        return (
            <Fragment key={`${salt}:field:${fieldType}:${fieldName}`}>
                <Field
                    {...omit(props, 'meta')}
                    {...typeProps}
                    label={label}
                    name={fieldName}
                    field={{
                        ...fieldProps,
                        value,
                        required
                    }}
                    {...(!!options ? {
                        options: options.map(({ key, value }) => ({
                            key,
                            value: key,
                            text: value
                        }))
                    } : null)}
                    {...(size(controlProps) ? { controlProps } : null)}
                    {...(unsettable ? {
                        controlContent: (
                            <Helper className="compact">
                                <UnsetButton
                                    onClick={() => update(null, true)}
                                    className="destructive">
                                    <FormattedMessage
                                        id="integration_action_secret_field_unset"
                                        defaultMessage="Clear this field’s stored value" />
                                </UnsetButton>
                            </Helper>
                        )
                    } : null)}
                    {...(unset ? {
                        controlContent: (
                            <Helper className="compact">
                                <span>
                                    <FormattedMessage
                                        id="integration_helper_secret_field_unset"
                                        defaultMessage="This field’s stored value will be cleared" />
                                </span>{' '}
                                <UnsetButton
                                    onClick={() => update(value, false)}
                                    className="constructive">
                                    <FormattedMessage
                                        id="action_edit"
                                        defaultMessage="Edit" />
                                </UnsetButton>
                            </Helper>
                        )
                    } : null)}
                    {...(onChange ? { onChange: ({ [fieldName]: value }) => update(value) } : null)}
                    {...((disabled || unset) ? { enabled: false } : null)}
                    {...(ref ? { ref } : null)}
                    salt={salt} />
                {(helper !== helperTranslationKey) && (
                    <Helper>{helper}</Helper>
                )}
                {[
                    !!size(disclaimer),
                    !error,
                    ((!!value && localValue !== false) || !!localValue || !!disclaimer?.includeWithoutValue),
                    (!!disclaimer?.excludeWhenConnected ? !isConnected : true)
                ].every(Boolean) && disclaimer.messages?.map((message, index) => (
                    <InlineMessage
                        {...omit(message, 'id')}
                        textClassName="preserve-line-breaks"
                        {...((index === disclaimer.messages.length - 1) ? { className: 'compact' } : null)}
                        key={`${salt}:disclaimer:${fieldName}:${message.id}`} />
                ))}
                {(divider && !isLastField) && <Divider />}
            </Fragment>
        )
    }
}

const getFieldNameLabel = (integration, fieldName) => {
    if(integration.type === '24sevenoffice' && fieldName === 'token') {
        return 'apiKey'
    }

    if(integration.type === 'altinn' && fieldName === 'lastFetchDate') {
        return 'altinn_last_fetch_date'
    }

    if(integration.type === 'unimicro' && fieldName === 'organizationNumber') {
        return 'unimicro_organization_number'
    }

    if(integration.type === 'unimicro' && fieldName === 'subEntityOrganizationNumber') {
        return 'unimicro_subentity_organization_number'
    }

    return fieldName
}

export const getCategoryTranslation = category => ({
    accounting: {
        id: 'integration_category_accounting',
        defaultMessage: 'Accounting'
    },
    authentication: {
        id: 'integration_category_authentication',
        defaultMessage: 'Authentication'
    },
    recruitment: {
        id: 'integration_category_recruitment',
        defaultMessage: 'Recruitment'
    },
    government: {
        id: 'integration_category_government',
        defaultMessage: 'Government'
    },
    notification: {
        id: 'integration_category_notification',
        defaultMessage: 'Notification'
    },
    other: {
        id: 'integration_category_other',
        defaultMessage: 'Other'
    }

})[category] ?? null

export const useCategories = () => {
    const { formatMessage } = useIntl()

    const { configuration } = useConfiguration()
    const [categories, setCategories] = useState([])

    useLayoutEffect(() => {
        setCategories([
            ...(configuration?.enums.integrationCategory ?? []).map(value => ({
                value,
                text: formatMessage(getCategoryTranslation(value))
            }))
        ])
    }, [])

    return {
        categories,
        getCategoryLabel: value => categories.find(category => category.value === value)?.text
    }
}